import { Link } from 'gatsby';
import { FileCode2Icon, PodcastIcon, ScrollTextIcon } from 'lucide-react';
import React from 'react';
import { Index, PoweredBy, useHits, useStats } from 'react-instantsearch';
import { Box, Text, useColorMode } from 'theme-ui';

interface CustomHitsProps {
  hitComponent: React.ComponentType<{
    hit: Hit;
    sendEvent: (eventName: string) => void;
  }>;
}

function CustomHits({ hitComponent: HitComponent }: CustomHitsProps) {
  const { items, sendEvent } = useHits<Hit>();

  return (
    <Box my={3}>
      {items.map((hit) => (
        <HitComponent key={hit.objectID} hit={hit} sendEvent={sendEvent} />
      ))}
    </Box>
  );
}

export interface Hit {
  objectID: string;
  slug: string;
  title: string;
  excerpt?: string;
  type: 'article' | 'letter' | 'podcast';
  abstract?: string;
  publishedAt: string;
}

export interface IndexType {
  name: string;
}

interface HitsInIndexProps {
  index: IndexType;
}

interface SearchResultProps {
  indices: IndexType[];
  show?: boolean;
}

const HitCount: React.FC = () => {
  const { nbHits } = useStats();

  return nbHits > 0 ? (
    <Text color="muted" sx={{ textAlign: 'right' }}>
      {nbHits} resultado{nbHits !== 1 ? 's' : ''}
    </Text>
  ) : null;
};

const PageHit: React.FC<{ hit: Hit }> = ({ hit }) => {
  const siteUrl = process.env.GATSBY_SITE_URL || 'http://localhost:8000';
  const fullUrl =
    hit.type === 'podcast'
      ? `${siteUrl}/podcast/${hit.slug}`
      : `${siteUrl}/article/${hit.slug}`;

  return (
    <Link to={fullUrl} style={{ textDecoration: 'none' }}>
      <Box
        sx={{
          display: 'flex',
          p: 3,
          bg: 'highlight',
          mb: 1,
          borderRadius: '8px',
          color: 'text',
        }}
      >
        <Box>
          {hit?.type === 'article' && (
            <FileCode2Icon
              size={18}
              style={{ marginRight: 8, marginTop: 1 }}
            />
          )}

          {hit?.type === 'letter' && (
            <ScrollTextIcon
              size={18}
              style={{ marginRight: 8, marginTop: 1 }}
            />
          )}

          {hit?.type === 'podcast' && (
            <PodcastIcon
              size={18}
              style={{ marginRight: 8, marginTop: 1 }}
            />
          )}
        </Box>
        <Box>
          <Text
            sx={{
              fontSize: 2,
              fontWeight: 'bold',
              color: 'primary',
            }}
          >
            {hit.title}
          </Text>

          <Text sx={{ fontSize: 1, color: 'textMuted', fontStyle: 'italic' }}>
            <span> - </span> {hit.publishedAt}
          </Text>
        </Box>
      </Box>
    </Link>
  );
};

const HitsInIndex: React.FC<HitsInIndexProps> = ({ index }) => (
  <Index indexName={index.name}>
    <Box sx={{ mb: 4 }}>
      <CustomHits hitComponent={PageHit} />
      <HitCount />
    </Box>
  </Index>
);

const SearchResult: React.FC<SearchResultProps> = ({ indices, show }) => {
  const [colorMode] = useColorMode();

  return (
    <>
      {show ? (
        <>
          {indices.map((index) => (
            <HitsInIndex index={index} key={index.name} />
          ))}
        </>
      ) : (
        <Box mt={3}>
          <Text color="muted" sx={{ size: 0.5 }}>
            Comienza a buscar artículos, autores o temas. Usa comillas para buscar
            frases exactas.
          </Text>
        </Box>
      )}

      <Box sx={{ mt: 4, maxWidth: '140px' }}>
        <PoweredBy theme={colorMode === 'dark' ? 'dark' : 'light'} />
      </Box>
    </>
  );
};

export default SearchResult;
